import { ALLERGENS, type Allergen, type Ingredient, type Recipe } from './types';
/** Walk the live recipe graph once; cycles and repeated subrecipes cannot duplicate sheets. */
export function recipeDependencies(recipeId: string, recipes: readonly Recipe[]): Recipe[] {
  const byId = new Map(recipes.map(r => [r.recipeId, r]));
  const seen = new Set([recipeId]); const found: Recipe[] = [];
  const visit = (id: string) => {
    for (const c of byId.get(id)?.components ?? []) {
      if (c.ref.kind !== 'recipe' || seen.has(c.ref.recipeId)) continue;
      seen.add(c.ref.recipeId); const child = byId.get(c.ref.recipeId);
      if (child) { found.push(child); visit(child.recipeId); }
    }
  };
  visit(recipeId); return found;
}
export function ingredientDependents(id: string, recipes: readonly Recipe[]): Array<{ recipe: Recipe; direct: boolean }> {
  const direct = new Set(recipes.filter(r => r.components.some(c => c.ref.kind === 'ingredient' && c.ref.ingredientId === id)).map(r => r.recipeId));
  return recipes.filter(r => direct.has(r.recipeId) || recipeDependencies(r.recipeId, recipes).some(s => direct.has(s.recipeId)))
    .map(recipe => ({ recipe, direct: direct.has(recipe.recipeId) }));
}
/** Computed from current source records, never a stale copy saved in parent recipes. */
export function recipeAllergens(recipeId: string, recipes: readonly Recipe[], ingredients: readonly Ingredient[]): Allergen[] {
  const root = recipes.find(r => r.recipeId === recipeId); if (!root) return [];
  const byId = new Map(ingredients.map(i => [i.ingredientId, i])); const result = new Set<Allergen>();
  for (const recipe of [root, ...recipeDependencies(recipeId, recipes)]) {
    for (const a of recipe.allergens ?? []) result.add(a);
    for (const c of recipe.components) if (c.ref.kind === 'ingredient') for (const a of byId.get(c.ref.ingredientId)?.allergens ?? []) result.add(a);
  }
  return ALLERGENS.filter(a => result.has(a));
}

/** Explica qué ingredientes o subrecetas hacen que un alérgeno sea heredado. */
export function recipeInheritedAllergenSources(
  recipeId: string,
  recipes: readonly Recipe[],
  ingredients: readonly Ingredient[],
): Partial<Record<Allergen, string[]>> {
  const byRecipe = new Map(recipes.map(recipe => [recipe.recipeId, recipe]));
  const byIngredient = new Map(ingredients.map(ingredient => [ingredient.ingredientId, ingredient]));
  const sources = new Map<Allergen, Set<string>>();
  const seen = new Set<string>();
  const add = (allergen: Allergen, source: string) => {
    const current = sources.get(allergen) ?? new Set<string>();
    current.add(source);
    sources.set(allergen, current);
  };
  const visit = (id: string, root: boolean) => {
    if (seen.has(id)) return;
    seen.add(id);
    const current = byRecipe.get(id);
    if (!current) return;
    if (!root) for (const allergen of current.allergens ?? []) add(allergen, `preparación ${current.name}`);
    for (const component of current.components) {
      if (component.ref.kind === 'ingredient') {
        const ingredient = byIngredient.get(component.ref.ingredientId);
        if (ingredient) for (const allergen of ingredient.allergens ?? []) add(allergen, ingredient.name);
      } else {
        visit(component.ref.recipeId, false);
      }
    }
  };
  visit(recipeId, true);
  return Object.fromEntries(
    ALLERGENS.flatMap(allergen => {
      const values = sources.get(allergen);
      return values ? [[allergen, [...values]]] : [];
    }),
  );
}
/**
 * Pistas por el nombre («Por el nombre, revisa: Lácteos»). Solo pistas: una
 * persona confirma con la etiqueta del producto. Ampliada en la revisión de
 * Alex (12 oct 2026): la burrata no avisaba de Lácteos ni el calamar de
 * Moluscos. Los nombres van sin acentos y en minúscula.
 */
const PISTAS_ALERGENOS: Array<[Allergen, RegExp]> = [
  ['gluten', /\b(trigo|harinas?(?! de (maiz|arroz|garbanzo|almendra|coco|yuca|tapioca|avena sin gluten))|pan|panes|pan rallado|pan molido|panko|pastas?(?! de (tomate|achiote|ajo|chiles?|curry|miso|aceitunas?))|espaguetis?|spaghetti|macarrones|fideos?|tallarines|lasana|cuscus|couscous|bulgur|seitan|cebada|centeno|avena|espelta|kamut|malta|semola|cerveza|galletas?|bizcochos?|brioche|croissants?|hojaldre|empanizador|rebozador|tortillas? de harina|obleas?)\b/],
  ['crustaceos', /\b(gambas?|langostinos?|camaron(es)?|cangrejos?|jaibas?|langostas?|bogavantes?|cigalas?|carabineros?|necoras?|percebes?|krill|crustaceos?)\b/],
  ['huevos', /\b(huevos?|mayonesa|merengues?|claras?|yemas?|alioli|aioli)\b/],
  ['pescado', /\b(pescados?|salmon(es)?|atun(es)?|bacalao|merluza|anchoas?|boquerones|boqueron|sardinas?|lubina|rape|truchas?|tilapia|robalo|huachinango|mero|caballa|bonito|surimi|pez|peces|lenguado|corvina|pargo|salsa de pescado)\b/],
  ['cacahuetes', /\b(cacahuetes?|cacahuates?|mani|manies)\b/],
  ['soja', /\b(soja|soya|tofu|edamame|miso|tamari)\b/],
  ['lacteos', /\b(leche(?! de (coco|almendras?|avena|soja|soya|arroz))|lacteos?|quesos?|mantequilla|yogur|yogurt|yogures|nata|crema(?! de (cacahuate|cacahuete|mani|coco))|burrata|mozzarella|mozarela|parmesano|ricotta|ricota|mascarpone|requeson|cuajada|kefir|ghee|suero de leche|lactosa|provolone|gouda|cheddar|brie|camembert|manchego|feta|panela|helados?|bechamel)\b/],
  ['frutos_secos', /\b(almendras?|nuez|nueces|avellanas?|pistachos?|anacardos?|maranon(es)?|pecanas?|macadamias?|pinon(es)?|frutos secos)\b/],
  ['apio', /\bapio\b/],
  ['mostaza', /\bmostaza\b/],
  ['sesamo', /\b(sesamo|ajonjoli|tahini|tahin)\b/],
  ['sulfitos', /\b(vinos?|vinagres?|sulfitos?|pasas)\b/],
  ['altramuces', /\b(altramuz|altramuces|lupinos?)\b/],
  ['moluscos', /\b(calamar(es)?|chipiron(es)?|pulpos?|sepias?|chocos?|potas?|mejillon(es)?|almejas?|ostras?|ostion(es)?|vieiras?|berberechos?|navajas?|caracol(es)?|abulon(es)?|callos? de hacha|moluscos?)\b/],
];

/** Conservative word hints only; a person must confirm the actual product's label. */
export function suggestIngredientAllergens(name: string): Allergen[] {
  const text = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  return PISTAS_ALERGENOS.filter(([, pattern]) => pattern.test(text)).map(([a]) => a);
}
